import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { inject } from 'mobx-react';
import { routerShape, withRouter } from 'react-router';
import { withI18n } from 'spa/Components/Context';
import { BOOKING_BASE_URL } from 'utils/constants';
import uuidv4 from 'uuid/v4';
import * as Sentry from '@sentry/react';
import isValidException from 'utils/isValidException';

import BasketSummaryItems from 'spa/Pages/BookingNext/Components/BasketSidePanelSummary/BasketSummaryItems';
import BlockOptionsList from '@urbanmassage/component-library/lib/BlockOptionsList/BlockOptionsList';
import Button, { ButtonThemeTypes } from '@urbanmassage/component-library/lib/Button/Button';
import DocumentMeta from 'spa/Components/DocumentMeta';
import NotFound from 'spa/Pages/NotFound';
import TreatmentSingleBookSection from 'spa/Pages/Treatments/TreatmentSingleBookSection';
import Typography, { TypeVariants } from '@urbanmassage/component-library/lib/Typography/Typography';

import './book-treatment-page.less';

const BookTreatmentPage = ({
  basketNew,
  basketHasAddress,
  categories: {
    getV2SpecialityContentByStub,
    getV2SpecialityDurationsByStub,
  },
  notifications,
  router,
  tracker,
  location,
}, { i18n }) => {
  const [durations, setDurations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDurationId, setSelectedDurationId] = useState(null);
  const [specialityBlocks, setSpecialityBlocks] = useState([]);
  const [specialityStub, setSpecialityStub] = useState(null);
  const [speciality, setSpeciality] = useState(null);
  const [specialityNotFound, setSpecialityNotFound] = useState(false);
  const [showBasketSummary, setShowBasketSummary] = useState(false);
  const [basketServices, setBasketServices] = useState([]);
  const [isLoadingRemoveService, setIsLoadingRemoveService] = useState(false);

  const onChangeDuration = (duration_id) => {
    tracker.track('Selected Option In Book Treatment Page', {
      duration_id,
      speciality_id: speciality && speciality.id,
    });

    const [selectedOption] = durations.filter(durationSingle => durationSingle.id === duration_id);

    if (selectedOption && selectedOption.isRecommendedOption) {
      tracker.track('Selected Recommended Option In Book Treatment Page', {
        duration_id,
        speciality_id: speciality && speciality.id,
      });
    }

    setSelectedDurationId(duration_id);

    setSpecialityBlocks([{
      link_id: uuidv4(),
      speciality_id: speciality && speciality.id,
      duration_id: parseInt(duration_id, 10),
    }]);
  };

  const loadBasket = () => {
    setIsLoading(true);

    basketNew.basketsGetFull.load()
      .then((response) => {
        if (response.appointment && response.appointment.services) {
          const tempServices = response.appointment.services.map(service => ({
            id: service.id,
            specialityName: service.speciality.title,
            subtitle: service.duration.title,
          }));

          setBasketServices(tempServices);
        }

        setIsLoading(false);
      }).catch((err) => {
        if (__DEV__) {
          console.error(err);
        }

        if (err && err.json && err.json.httpCode === 404) {
          setShowBasketSummary(false);
        } else {
          notifications.add({
            id: 'getFullBasketError',
            type: 'error',
            title: i18n.__error(err),
          });
        }

        // This is unexpected so record it in Sentry
        if (isValidException(err)) {
          Sentry.captureException(err);
        } else {
          Sentry.captureMessage('BookTreatmentPage - unhandled error loading basket info');
        }

        setIsLoading(false);
      });
  };

  const onClickBookButton = (event) => {
    if (event) event.preventDefault();

    basketNew.basketsAppointmentsServices.put('', {
      specialities: [
        // Remove the key property off of the speciality blocks to submit to the api
        ...specialityBlocks.map(block => ({
          ...block,
          key: undefined,
        }))],
    }).then(() => {
      loadBasket();
      setShowBasketSummary(true);
    }).catch((err) => {
      notifications.add({
        id: 'basket-speciality',
        type: 'error',
        title: i18n.__error(err),
      });

      const errContexts = {
        contexts: {
          location: 'speciality-select',
        },
        extra: {
          code: err && err.json && err.json.code,
        },
      };

      // All of these codes are unexpected so record it in Sentry
      if (isValidException(err)) {
        Sentry.captureException(err, errContexts);
      } else {
        Sentry.captureMessage('Book treatment page POST error', errContexts);
      }
    });
  };

  const loadDurationOptions = () => {
    setIsLoading(true);

    getV2SpecialityDurationsByStub.load(specialityStub).then((response) => {
      if (response && response.durations && response.durations.length) {
        setSelectedDurationId(response.durations[0].id);
        setDurations(response.durations);
        onChangeDuration(response.durations[0].id);
        setIsLoading(false);
      }
    }).catch((err) => {
      setIsLoading(false);
      Sentry.captureMessage('Book treatment page faild to load duration options', err);
    });
  };

  const onClickRemoveService = (serviceId) => {
    tracker.track('Clicked remove service in book treatment page', {
      category: 'Booking',
    });

    setIsLoadingRemoveService(true);

    basketNew.basketsAppointmentsServicesDelete.put(serviceId).then(() => {
      setIsLoadingRemoveService(false);
      loadBasket();
    }).catch((error) => {
      if (__DEV__) {
        console.error(error);
      }

      notifications.add({
        id: 'removeServiceFromBasketError',
        type: 'error',
        title: i18n.__error(error),
      });

      // This is unexpected so record it in Sentry
      if (isValidException(error)) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage('Book treatment basket summary unhandled error removing service from basket');
      }

      setIsLoadingRemoveService(false);
    });
  };

  const onClickAddAnotherTreatment = () => {
    tracker.track('Clicked add another treatment in book treatment page', {
      category: 'Booking',
    });

    router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/services`);
  };

  const onClickChooseDateTime = () => {
    tracker.track('Clicked choose date and time in book treatment page', {
      category: 'Booking',
    });

    router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/service-availability`);
  };

  // Find speciality name
  useEffect(() => {
    const stub = window.location.pathname.split('/')[3];
    setSpecialityStub(stub);

    getV2SpecialityContentByStub.load(stub).then((response) => {
      setSpeciality(response.speciality);
    }).catch((err) => {
      if (err && err.json && err.json.httpCode === 404) {
        setSpecialityNotFound(true);
      }
    });
  }, []);

  // Get durations on load if basket has address
  useEffect(() => {
    if (basketHasAddress && speciality) {
      loadDurationOptions();
    }
  }, [basketHasAddress, speciality]);

  useEffect(() => {
    if (showBasketSummary && basketServices.length > 0) {
      tracker.track('Shown the basket summary in book treatment page', {
        category: 'Booking',
      });
    }
    basketNew.getBasketSummary();
  }, [showBasketSummary, basketServices]);

  if (specialityNotFound) {
    return <NotFound location={location} />;
  }

  return (
    <div className="book-treatment">
      <div className="container">
        <DocumentMeta
          meta={{ name: { robots: 'noindex' } }}
        />

        {showBasketSummary && (
          <React.Fragment>
            <div className="book-treatment__basket-summary-wrapper" data-testid="book-treatment__basket-summary-wrapper">
              {basketServices.length > 0 && (
                <React.Fragment>
                  <Typography
                    align="center"
                    component={TypeVariants.h1}
                    variant={TypeVariants.titleXXL}
                    className="book-treatment__title"
                    data-testid="book-treatment-title"
                  >
                    {i18n._x('Basket summary', 'Book treatment page title')}
                  </Typography>
                  <BasketSummaryItems
                    boxed
                    isLoadingRemoveService={isLoadingRemoveService}
                    itemsList={basketServices}
                    onClickRemoveService={onClickRemoveService}
                  />
                  <Button
                    block
                    theme={ButtonThemeTypes.ghost}
                    className="book-treatment__add-another-treatment-button"
                    data-testid="book-treatment__add-another-treatment-button"
                    onClick={onClickAddAnotherTreatment}
                  >
                    {i18n._x('Add another treatment', 'Book treatment page')}
                  </Button>
                  <Button
                    block
                    className="book-treatment__choose-date-time-button"
                    data-testid="book-treatment__choose-date-time-button"
                    onClick={onClickChooseDateTime}
                  >
                    {i18n._x('Choose date & time', 'Book treatment page')}
                  </Button>
                </React.Fragment>
              )}

              {basketServices.length === 0 && (
                <React.Fragment>
                  <div className="book-treatment__empty-basket-text-wrapper">
                    <Typography
                      align="center"
                      component={TypeVariants.h1}
                      variant={TypeVariants.titleXXL}
                      className="book-treatment__empty-basket-title"
                    >
                      {i18n._x('Basket summary', 'Book treatment page title')}
                    </Typography>
                    {!isLoading && (
                      <Typography
                        align="center"
                        component={TypeVariants.body}
                      >
                        {i18n._x('You have no treatments in your basket', 'Book treatment page')}
                      </Typography>
                    )}
                  </div>
                  {!isLoading && (
                    <Button block onClick={onClickAddAnotherTreatment}>
                      {i18n._x('Choose a service', 'Book treatment page')}
                    </Button>
                  )}
                </React.Fragment>
              )}
            </div>
          </React.Fragment>
        )}

        {!showBasketSummary && (
          <React.Fragment>
            {durations.length > 0 ? (
              <React.Fragment>
                <Typography
                  variant={TypeVariants.titleXXL}
                  align="center"
                  component={TypeVariants.h1}
                  className="book-treatment__title"
                  data-testid="book-treatment-title"
                >
                  {i18n._x('Confirm the details', 'Book treatment page title')}
                </Typography>
                <div className="book-treatment__durations-wrapper" data-testid="book-treatment__durations-wrapper">
                  <BlockOptionsList
                    options={durations.map(duration => ({
                      id: duration.id,
                      title: duration.name,
                      label: duration.isRecommendedOption ? i18n._x('POPULAR', 'Treatment recommended option') : null,
                    }))}
                    isLoading={isLoading}
                    selectedOptionId={selectedDurationId}
                    onChange={onChangeDuration}
                  />
                </div>
              </React.Fragment>
            ) : (
              <Typography
                align="center"
                component={TypeVariants.h1}
                variant={TypeVariants.titleXXL}
                className="book-treatment__title"
                data-testid="book-treatment-title"
              >
                {i18n._x('Where would you like your appointment?', 'Book treatment page title')}
              </Typography>
            )}
            {speciality && speciality.id && (
              <div className="book-treatment__address-select-wrapper" data-testid="book-treatment__address-select-wrapper">
                <TreatmentSingleBookSection onClickBookButton={onClickBookButton} specialityId={speciality.id} />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

BookTreatmentPage.propTypes = {
  basketNew: PropTypes.shape({
    basketsAppointmentsServicesDelete: PropTypes.shape({
      put: PropTypes.func.isRequired,
    }).isRequired,
    basketsAppointmentsServices: PropTypes.shape({
      put: PropTypes.func.isRequired,
    }).isRequired,
    basketsGetFull: PropTypes.shape({
      load: PropTypes.func.isRequired,
    }).isRequired,
    getBasketSummary: PropTypes.func.isRequired,
  }).isRequired,
  basketHasAddress: PropTypes.bool.isRequired,
  categories: PropTypes.shape({
    getV2SpecialityDurationsByStub: PropTypes.shape({
      load: PropTypes.func.isRequired,
    }).isRequired,
    getV2SpecialityContentByStub: PropTypes.shape({
      load: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  location: PropTypes.object.isRequired,
  notifications: PropTypes.shape({
    add: PropTypes.func.isRequired,
  }).isRequired,
  router: routerShape.isRequired,
  tracker: PropTypes.shape({
    track: PropTypes.func.isRequired,
  }).isRequired,
};


export default inject(({
  store: {
    basketNew,
    basketNew: {
      basketHasAddress,
    },
    categories,
    notifications,
    tracker,
  },
}) => ({
  basketNew,
  basketHasAddress,
  categories,
  notifications,
  tracker,
}))(withRouter(withI18n(BookTreatmentPage)));



// WEBPACK FOOTER //
// ./spa/Pages/BookingNext/BookTreatment/BookTreatmentPage.jsx